import type { Summer2026SaleMarketTier } from '../utils/get-summer-2026-sale-variant';
import { getSummer2026SaleMarketTier } from '../utils/get-summer-2026-sale-variant';

export type Summer2026SaleVariant = 'july_4th' | 'summer' | 'mid_year_sale';

export const SUMMER_2026_SALE_CTA_LABEL = 'Save Now';

/**
 * Save Now deep link, by market tier. Full-price markets (`SUMMER26US`, `SUMMER26G1`) check out on
 * the 4-month bundle; new/low-price markets (`SUMMER26G2`) on the monthly plan. Use `getSummer2026SaleCheckoutHref` to append the live coupon.
 */
export const SUMMER_2026_SALE_CHECKOUT_HREF: Record<Summer2026SaleMarketTier, string> = {
  full: '/checkout/individual/4month/credit-card',
  low: '/checkout/individual/monthly/credit-card',
};

type Summer2026SaleVariantConfig = {
  backgroundColor: string;
  catalogBg: string;
  catalogBgMobile?: string;
  catalogBorderColor: string;
  catalogBorderGradient?: string;
  catalogMobileObjectFit?: 'contain' | 'cover';
  catalogMobileObjectPosition?: string;
  ctaColor: string;
  experienceAtfBg: string;
  experienceAtfObjectFit: 'contain' | 'cover';
  experienceAtfObjectPosition: string;
  headlineAccentColor: string;
  headlineLine1: string;
  headlineLine2: string;
  homepageBg: string;
  homepageBgMobile?: string;
  homepageButtonHasRocketIcon: boolean;
  homepageMobileObjectFit?: 'contain' | 'cover';
  homepageMobileObjectPosition?: string;
  homepageObjectFit: 'contain' | 'cover';
  homepageObjectPosition: string;
  homepageTitleColor: string;
  subtitle: string;
  subtitleColor: string;
};

export const SUMMER_2026_SALE_VARIANTS: Record<Summer2026SaleVariant, Summer2026SaleVariantConfig> = {
  july_4th: {
    backgroundColor: '#00050e',
    catalogBg: '/images/promotions/july-4th-sale/catalog-bg.png',
    catalogBorderColor: '#175CFF',
    catalogBorderGradient: 'linear-gradient(135deg, #175CFF 0%, #7BA2FF 40%, #E5A5FF 100%)',
    ctaColor: '#175CFF',
    experienceAtfBg: '/images/promotions/july-4th-sale/experience-atf-bg.png',
    experienceAtfObjectFit: 'cover',
    experienceAtfObjectPosition: 'right center',
    headlineAccentColor: '#7BA2FF',
    headlineLine1: 'July 4th Sale:',
    headlineLine2: '50% off sitewide',
    homepageBg: '/images/promotions/july-4th-sale/homepage-bg.png',
    homepageBgMobile: '/images/promotions/july-4th-sale/homepage-bg-mobile.png',
    homepageButtonHasRocketIcon: true,
    homepageMobileObjectFit: 'cover',
    homepageMobileObjectPosition: 'right top',
    homepageObjectFit: 'cover',
    homepageObjectPosition: 'right center',
    homepageTitleColor: 'white',
    subtitle: 'Save on AI & tech courses, plus MS in AI & MBA programs.',
    subtitleColor: '#AFC6FF',
  },
  summer: {
    backgroundColor: 'white',
    catalogBg: '/images/promotions/summer-sale/catalog-bg.png',
    catalogBgMobile: '/images/promotions/summer-sale/catalog-bg-mobile.png',
    catalogBorderColor: '#C35000',
    catalogMobileObjectFit: 'cover',
    catalogMobileObjectPosition: 'bottom',
    ctaColor: '#175CFF',
    experienceAtfBg: '/images/promotions/summer-sale/atf-card-summer-promo-bg.png',
    experienceAtfObjectFit: 'cover',
    experienceAtfObjectPosition: 'center',
    headlineAccentColor: '#EE7622',
    headlineLine1: 'Summer Sale:',
    headlineLine2: '50% off sitewide',
    homepageBg: '/images/promotions/summer-sale/homepage-bg.png',
    homepageBgMobile: '/images/promotions/summer-sale/homepage-bg-mobile.png',
    homepageButtonHasRocketIcon: false,
    homepageMobileObjectFit: 'cover',
    homepageMobileObjectPosition: 'right top',
    homepageObjectFit: 'cover',
    homepageObjectPosition: 'right center',
    homepageTitleColor: 'black',
    subtitle: 'Save on AI & tech courses, plus MS & MBA programs.',
    subtitleColor: 'black',
  },
  mid_year_sale: {
    backgroundColor: 'white',
    catalogBg: '/images/promotions/summer-sale/catalog-bg.png',
    catalogBgMobile: '/images/promotions/summer-sale/catalog-bg-mobile.png',
    catalogBorderColor: '#C35000',
    catalogMobileObjectFit: 'cover',
    catalogMobileObjectPosition: 'bottom',
    ctaColor: '#175CFF',
    experienceAtfBg: '/images/promotions/summer-sale/atf-card-summer-promo-bg.png',
    experienceAtfObjectFit: 'cover',
    experienceAtfObjectPosition: 'center',
    headlineAccentColor: '#EE7622',
    headlineLine1: 'Mid-year sale',
    headlineLine2: 'is live sitewide',
    homepageBg: '/images/promotions/summer-sale/homepage-bg.png',
    homepageBgMobile: '/images/promotions/summer-sale/homepage-bg-mobile.png',
    homepageButtonHasRocketIcon: false,
    homepageMobileObjectFit: 'cover',
    homepageMobileObjectPosition: 'right top',
    homepageObjectFit: 'cover',
    homepageObjectPosition: 'right center',
    homepageTitleColor: 'black',
    subtitle: 'Save on AI & tech courses, plus MS & MBA programs.',
    subtitleColor: 'black',
  },
};

/**
 * Variant checkout link with the live coupon appended as `?coupon=<promoCode>`. The coupon also
 * auto-applies from the `COUPON_COOKIE` that `usePromotion` writes; the query param is a fallback
 * in case auto-apply does not run. When absent, the bare tier link is returned.
 */
export function getSummer2026SaleCheckoutHref(variant: Summer2026SaleVariant, promoCode?: string | null): string {
  const href = SUMMER_2026_SALE_CHECKOUT_HREF[getSummer2026SaleMarketTier(variant)];
  if (!promoCode) {
    return href;
  }
  const separator = href.includes('?') ? '&' : '?';
  return `${href}${separator}coupon=${encodeURIComponent(promoCode)}`;
}
